<template>
  <div>
    <div>
      <b-card
        no-body
      >
        <div>
          <b-row>
            <b-col
              cols="6"
              md="3"
              class="ml-2 mt-2"
            >
              <b-form-group
                label="Invoice Number"
                label-for="v-ref-no"
              >
                <b-form-input
                  id="v-ref-no"
                  v-model="filters['invoice_no']"
                  placeholder="Search"
                />
              </b-form-group>
            </b-col>
            <b-col
              class="mr-2 mt-3 d-flex justify-content-end"
            >
              <ReportTableButtons
                :items="rows"
                :json_fieldz="json_fields"
                :name="`Debit Note Invoices - Page ${page}.xls`"
                :bulkname="`Debit Note Invoices.xls`"
                :fetch="fetchInvoicesForMerchantNoPagination"
                :show-refresh-button="false"
              />
            </b-col>
          </b-row>
          <!-- table -->
          <b-overlay
            id="overlay-background"
            blur="10px"
            opacity="0.30"
            rounded="sm"
            variant="light"
          >
            <b-table
              :fields="fields"
              :items="rows"
              :per-page="perPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              hover
              responsive
              striped
              show-empty
            >
              <template #empty>
                <TableDataFetching
                  :rows="items"
                  :data-loading="dataLoading"
                />
              </template>
              <template #head()="{ field }">
                <div
                  class="sortable-header"
                >
                  <span class="header-text">{{ field.label }}</span>
                  <feather-icon
                    v-if="field.sortable"
                    :icon="sort === field.key
                      ? 'ArrowUpIcon'
                      : 'ArrowDownIcon'"
                    size="14"
                  />
                </div>
              </template>
              <template #head(id)>
                <div class="d-flex align-items-start">
                  <div>
                    <b-form-checkbox
                      id="selectAllCheckbox"
                      v-model="selectAllCheckbox"
                      name="selectAllCheckbox"
                      @change="onClickSelectAllCheckbox()"
                    />
                  </div>
                </div>
              </template>
              <template #cell(id)="data">
                <div class="d-flex align-items-start">
                  <div>
                    <div class="font-weight-bolder">
                      <b-form-checkbox
                        v-model="depositSelected"
                        :value="data.item.id"
                      />
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(merchant.name)="data">
                <div class="d-flex align-items-start">
                  <div>
                    <div class="">
                      {{ `${data.value}${data.item.merchant.merchant_setting.merchant_type === 'cash' ? "&nbsp;[&nbsp;C&nbsp;]" : ''}` }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(merchant_business)="data">
                <span>{{ data.item.merchant_business || '---' }}</span>
              </template>
              <template #cell(invoice_no)="data">
                <div class="d-flex align-items-center">
                  <b-avatar
                    :text="avatarText(data.item.invoice_no)"
                    size="32"
                    variant="light-primary"
                  />
                  <div class="ml-1">
                    <div
                      class="font-weight-bolder text-primary"
                      @click="onClickInvoice(data.item.id)"
                    >
                      {{ data.value }}
                    </div>
                    <div class="font-small-2">
                      {{ __dateTimeFormatter(data.item.created_at) }}
                    </div>
                  </div>
                </div>
              </template>
              <template #cell(commission_rate)="data">
                <span>{{ __numberWithCommas(data.value, true) }}</span>
              </template>
              <template #cell(collected_cod)="data">
                <span>{{ __numberWithCommas(data.value, true) }}</span>
              </template>
              <template #cell(delivery_charge)="data">
                <span>{{ __numberWithCommas(data.value, true) }}</span>
              </template>
              <template #cell(return_charge)="data">
                <span>{{ __numberWithCommas(data.value, true) }}</span>
              </template>
              <template #cell(commission_amount)="data">
                <span>{{ __numberWithCommas(data.value, true) }}</span>
              </template>
              <template #cell(total_tax_applied)="data">
                <span>{{ __numberWithCommas(data.value, true) }}</span>
              </template>
              <template #cell(invoice_amount)="data">
                <span>{{ __numberWithCommas(data.value, true) }}</span>
              </template>
              <template #cell(invoice_amount_after_taxes)="data">
                <span>{{ __numberWithCommas(data.value, true) }}</span>
              </template>
              <template #cell(deposited_date)="data">
                {{ __dateTimeFormatter(data.value) || '---' }}
              </template>
              <template #cell(approved_date)="data">
                {{ __dateTimeFormatter(data.value) || '---' }}
              </template>
              <template #cell(finance_current_status.key)="data">
                <finance-status-badge
                  :status-key="data.value"
                  :finance-status="statusOptions"
                />
                <div
                  v-if="data.item.is_reversed_deposit"
                  class="mt-1 d-flex justify-content-center"
                >
                  <b-tooltip
                    :target="data.item.id.toString()"
                    triggers="hover"
                    variant="danger"
                  >
                    Rejected Once
                  </b-tooltip>
                  <b-avatar
                    :id="data.item.id.toString()"
                    size="20"
                    style="background-color: #ffb5b5"
                  >
                    <feather-icon
                      icon="ChevronsLeftIcon"
                      class="text-body align-middle"
                    />
                  </b-avatar>
                </div>
              </template>
            </b-table>
            <div class="mx-2 mb-2">
              <b-row
                cols="12"
              >
                <b-col
                  class="d-flex align-items-center justify-content-sm-start"
                  md="3"
                >
                  <label class="width-75">Per page</label>
                  <v-select
                    v-model="perPage"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="pageOptions"
                    :clearable="false"
                    class="per-page-selector d-inline-block width-100"
                  />
                </b-col>
                <!-- Pagination -->
                <b-col
                  class="d-flex align-items-center justify-content-sm-end"
                  md="9"
                >
                  <span class="text-muted pagination-text mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
                  <b-pagination
                    v-model="page"
                    :per-page="perPage"
                    :total-rows="(meta.total)? meta.total : 0"
                    class="mb-0 mt-1 mt-sm-0"
                    first-number
                    last-number
                    next-class="next-item"
                    prev-class="prev-item"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                    <template #next-text>
                      <feather-icon
                        icon="ChevronRightIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>

                </b-col>

              </b-row>
            </div>
          </b-overlay>
        </div>
      </b-card>
    </div>
    <view-order-modal ref="viewOrderModal" />
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BPagination,
  BRow,
  BOverlay,
  VBModal,
  BTable,
  VBTooltip,
  BAvatar,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'
import vSelect from 'vue-select'
import FinanceStatusBadge from '@core/components/status/FinanceStatusBadge.vue'
import ViewOrderModal from '@/components/AllOrders/ViewOrderModal.vue'
import { avatarText } from '@core/utils/filter'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import store from '@/store/index'

const FinanceRepository = RepositoryFactory.get('finance')

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    BAvatar,
    BPagination,
    BFormGroup,
    BFormInput,
    BCard,
    BRow,
    BCol,
    BTable,
    BOverlay,
    vSelect,
    ViewOrderModal,
    FinanceStatusBadge,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage],
  data() {
    return {
      id: null,
      avatarText,
      filters: {},
      total: 0,
      meta: {},
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50],
      totalRows: 1,
      page: 1,
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      form: {},
      showUpdateModal: false,
      modelLoading: false,
      filterOn: [],
      infoModal: {
        id: 'info-modal',
        title: '',
        content: '',
      },
      pageLength: 20,
      dir: false,
      rows: [],
      searchTerm: '',
      statusOptions: [],
      fields: [
        {
          key: 'id',
          sortable: false,
        },
        {
          key: 'invoice_no',
          label: 'Invoice',
          sortable: true,
        },
        {
          key: 'merchant.ref_no',
          label: 'Merchant ref no',
          sortable: true,
        },
        {
          key: 'merchant.name',
          label: 'Merchant',
          sortable: true,
        },
        {
          key: 'merchant_business',
          label: 'Merchant Business',
          sortable: true,
        },
        {
          key: 'commission_rate',
          label: 'Commission Rate',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'collected_cod',
          label: 'Collected COD',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'commission_amount',
          label: 'Commission Amount',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: false,
        },
        {
          key: 'delivery_charge',
          label: 'Delivery Charge',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'return_charge',
          label: 'Return Charge',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'invoice_amount',
          label: 'Invoice Amount',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'total_tax_applied',
          label: 'Total Tax Amount',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'invoice_amount_after_taxes',
          label: 'After Tax Amount',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
        {
          key: 'deposited_date',
          label: 'Paid Date',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: true,
        },
        {
          key: 'approved_date',
          label: 'Approved Date',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: true,
        },
        {
          key: 'finance_current_status.key',
          label: 'Status',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: true,
        },
      ],
      json_fields: {
        'Invoice NO': 'invoice_no',
        'Created Date': {
          field: 'created_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'Merchant Reference Number': {
          field: 'merchant.ref_no',
          callback: value => `"${value}"`,
        },
        Merchant: {
          field: 'merchant.name',
          callback: value => `"${value}"`,
        },
        'Merchant Business': {
          field: 'merchant_business',
          callback: value => `"${value}"`,
        },
        'Commission Rate': 'commission_rate',
        'Collected COD': 'collected_cod',
        'Commission Amount': 'commission_amount',
        'Delivery Charge': 'delivery_charge',
        'Return Charge': 'return_charge',
        'Invoice Amount': 'invoice_amount',
        'Total Tax Amount': 'total_tax_applied',
        'After Tax Amount': 'invoice_amount_after_taxes',
        'Paid Date': {
          field: 'deposited_date',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'Approved Date': {
          field: 'approved_date',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        Status: 'finance_current_status.name',
      },
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    sortOptions() {
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({
          text: f.label,
          value: f.key,
        }))
    },
  },
  watch: {
    page() {
      this.fetchInvoicesForMerchant()
    },
    perPage() {
      this.fetchInvoicesForMerchant()
    },
  },
  created() {
    this.id = this.$route.params.merchantDebitNote || this.$route.params.id
  },
  mounted() {
    this.statusOptions = JSON.parse(localStorage.getItem('finance_statuses') || '[]')
    this.fetchInvoicesForMerchant()
  },
  methods: {
    async fetchInvoicesForMerchant() {
      if (!this.id) {
        this.id = this.$route.params.merchantDebitNote || this.$route.params.id
      }
      if (!this.id) return

      this.dataLoading = true
      this.rows = []
      try {
        const { data } = await FinanceRepository.getMerchantDebitNoteInvoices(this.page, this.perPage, this.id, this.filterQuery)
        this.rows = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchInvoicesForMerchantNoPagination() {
      try {
        const { data } = await FinanceRepository.getMerchantDebitNoteInvoicesNoPagination(this.id, this.filterQuery || '')
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    resetModal() {
      this.clearOnClick()
    },
    filterQueryUpdate() {
      this.fetchInvoicesForMerchant()
    },
    handleChangePage(page) {
      this.page = page
      this.fetchInvoicesForMerchant()
    },
    changePage(value) {
      this.perPage = value
    },
    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`
      this.infoModal.content = JSON.stringify(item, null, 2)
      this.$root.$emit('bv::show::modal', this.infoModal.id, button)
    },
    resetInfoModal() {
      this.infoModal.title = ''
      this.infoModal.content = ''
    },
    onClickViewOrder(id) {
      this.$refs.viewOrderModal.openModal(id)
    },
    onClickInvoice(invoice) {
      this.$router.push({
        name: 'merchant-finance-invoice',
        params: {
          id: invoice,
        },
      })
    },
  },
}
</script>
<style lang="scss">
@import '../../../@core/scss/vue/libs/vue-select.scss';
@media (max-width: 767px) {
  .pagination-text {
    font-size: 0.75rem;
  }
}
</style>
